'use client'

import { useState } from 'react'
import { Play } from 'lucide-react'

interface UserSetupProps {
  onComplete: (name: string, avatar: string) => void
}

const AVATAR_OPTIONS = [
  '🧑‍💻', '👨‍🎓', '👩‍🎓', '🧑‍🎨', '👨‍🚀', '👩‍🚀', 
  '🧙‍♂️', '🧙‍♀️', '🦸‍♂️', '🦸‍♀️', '👨‍🎤', '👩‍🎤',
  '🧑‍🍳', '👨‍⚕️', '👩‍⚕️', '🧑‍🔬', '👨‍🏫', '👩‍🏫',
  '😊', '😎', '🤓', '😇', '🥳', '🤩',
  '🐱', '🐶', '🦊', '🐼', '🐨', '🦁'
]

export function UserSetup({ onComplete }: UserSetupProps) {
  const [name, setName] = useState('')
  const [selectedAvatar, setSelectedAvatar] = useState(AVATAR_OPTIONS[0])
  const [step, setStep] = useState<'name' | 'avatar'>('name')

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (name.trim()) {
      setStep('avatar')
    }
  }

  const handleAvatarSelect = (avatar: string) => {
    setSelectedAvatar(avatar)
  }

  const handleComplete = () => {
    if (name.trim() && selectedAvatar) {
      onComplete(name.trim(), selectedAvatar)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-400 via-dark-300 to-dark-200 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 rounded-lg">
              <Play className="w-8 h-8 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
              AnimeTine
            </span>
          </div>
          <p className="text-gray-400">Welcome to your anime destination!</p>
        </div>

        {step === 'name' && (
          <div className="bg-gray-800 rounded-xl p-8 shadow-2xl border border-gray-700">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">What's your name?</h2>
              <p className="text-gray-400">Let's personalize your anime experience</p>
            </div>

            <form onSubmit={handleNameSubmit}>
              <div className="mb-6">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 bg-dark-300 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-center text-lg"
                  autoFocus
                  maxLength={20}
                />
              </div>

              <button
                type="submit"
                disabled={!name.trim()}
                className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold rounded-lg transition-all duration-200 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </form>
          </div>
        )}

        {step === 'avatar' && (
          <div className="bg-gray-800 rounded-xl p-8 shadow-2xl border border-gray-700">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Choose your avatar</h2>
              <p className="text-gray-400">Pick one that represents you, {name}!</p>
            </div>

            <div className="grid grid-cols-6 gap-3 mb-8">
              {AVATAR_OPTIONS.map((avatar) => (
                <button
                  key={avatar}
                  onClick={() => handleAvatarSelect(avatar)}
                  className={`w-12 h-12 rounded-lg text-2xl flex items-center justify-center transition-all duration-200 ${
                    selectedAvatar === avatar
                      ? 'bg-blue-600 ring-2 ring-blue-400 ring-offset-2 ring-offset-gray-800 scale-110'
                      : 'bg-gray-700 hover:bg-gray-600 hover:scale-105'
                  }`}
                >
                  {avatar}
                </button>
              ))}
            </div>

            <div className="text-center mb-6">
              <div className="inline-flex items-center space-x-3 px-4 py-2 bg-gray-700 rounded-lg">
                <span className="text-2xl">{selectedAvatar}</span>
                <span className="text-white font-medium">{name}</span>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setStep('name')}
                className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleComplete}
                className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-lg transition-all duration-200"
              >
                Start Watching!
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
